import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import moment from 'moment';

import { AnalyticsService, ICreateAppointmentPayload, PartnerBrandService, } from '@campfire-nx/api';

import { AppointmentService } from './appointment.service';
import { AuthService } from '@open-portal-app/core/services/auth.service';

import { IStep, ISection, IElement, ITimeslots, IUserDetails, IFilteredTimeslots, IFlowPayload, IInfoBox } from '../models/appointments.models'
import { COMMON_CONSTANTS } from '@open-portal-app/core/constants/common.constants';
import { STEP_ONE, STEP_TWO, STEP_THREE, STEP_FOUR, ERRORS, TOPICS } from '../constants/appointment-flow-v001';

@Injectable({
  providedIn: 'root',
})
export class AppointmentFlowService {
  steps = [] as IStep[];
  currentStepIndex = 0;
  appointmentDetails = this.appointmentService.details;
  customTimeStringPrefix = 'time--';
  errorCode!: string;
  availableTimeslots!: IFilteredTimeslots[];
  userDetails?: IUserDetails;

  // change this value to increase or decrease the amount of timeslots
  maxAppointmentTimeslots = 4;

  constructor(
    private router: Router,
    private appointmentService: AppointmentService,
    private partnerBrandService: PartnerBrandService,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
  ) {}

  next(payload: IFlowPayload) {
    return new Promise((resolve) => {
      if (this.currentStepIndex <= 1) {
        // update the current step so we have the data if we go back to it
        this.updateCurrentStep(payload);

        // update the next step in the flow then move to the next step
        this.currentStepIndex++;
        this.updateCurrentStep(payload);
        resolve(this.steps[this.currentStepIndex]);
      } else if (this.currentStepIndex === 2) {
        this.analyticsService.trackEvent('BookAppointment', 'AppointmentFlow', 'create-booking', {}, this.steps[this.currentStepIndex].analytics);
        this.bookAppointment(payload, resolve);
      } else if (this.currentStepIndex >= 3) {
        // Reset currentStepIndex and go to dashboard
        this.currentStepIndex = 0;
        if (this.authService.isAuthenticated()) {
          this.router.navigate(['/dashboard']);
        } else {
          const companyInfo = this.partnerBrandService.getAttribute('companyInfo', 'partner');
          window.open(`https://${companyInfo.BASE_DOMAIN}`, '_self');
        };
      }
    });
  }

  back() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex = this.currentStepIndex - 1;
    } else if (this.currentStepIndex === 0) {
      return new Promise((resolve) => {
        if (this.appointmentDetails.raptorSource) {
          switch (this.appointmentDetails.raptorSource) {
            case 'quoteFlow':
              this.router.navigate([`/quote/${this.appointmentDetails.entity_ref}`], { replaceUrl: true });
              break;
            case 'endorsementFlow':
              this.router.navigate([`/endorsement/${this.appointmentDetails.entity_ref}`], { replaceUrl: true });
              break;
            case 'cancellationFlow':
              this.router.navigate([`/cancellation/${this.appointmentDetails.entity_ref}`], { replaceUrl: true });
              break;
            case 'prerenwalFlow':
              this.router.navigate([`/prerenewal/${this.appointmentDetails.entity_ref}`], { replaceUrl: true });
              break;
            default:
              this.router.navigate(['/support']);
              break;
          }
        } else {
          this.router.navigate(['/support']);
        }
      });
    }

    return new Promise((resolve) => resolve(this.steps[this.currentStepIndex]));
  }

  // Creates the first step from BE timeslots and user details
  getFirstStep(timeslots: ITimeslots, userDetails?: IUserDetails) {
    this.userDetails = userDetails;
    const stepOne = this.createStepOne(timeslots, userDetails);
    this.steps = [stepOne, STEP_TWO, STEP_THREE, STEP_FOUR];

    return this.steps[0];
  }

  createStepOne(
    timeslots: ITimeslots,
    userDetails?: IUserDetails
  ) {
    let stepOne = {} as IStep;
    let dateQuestion = {} as any;
    let timeslotQuestions = [] as any;

    if (timeslots.error) {
      this.errorCode = 'UNKNOWN';
    } else if (Object.keys(timeslots).length === 0) {
      this.errorCode = 'NO_TIMESLOTS';
    }

    this.availableTimeslots = this.getAvailableTimeslots(this.errorCode === 'UNKNOWN' ? {} : timeslots);

    // Configure Date question's choices
    dateQuestion = this.createDateQuestion(this.availableTimeslots);

    // Configure Time question's choices
    for (const timeslot in this.availableTimeslots) {
      if (this.availableTimeslots.hasOwnProperty(timeslot)) {
        const { date, timeslots } = this.availableTimeslots[timeslot];
        timeslotQuestions.push(this.createTimeslotQuestions(date, timeslots));
      }
    }

    // Applying the created date and timeslot questions to the first step
    stepOne = {
      ...STEP_ONE,
      sections: STEP_ONE.sections.map((section: any) => {
        if (section.uuid === 'available_appointments_section') {
          return {
            ...section,
            elements: [dateQuestion, ...timeslotQuestions],
          };
        }

        return section;
      }),
      data: {
        ...STEP_ONE.data,
        ...userDetails,
        date: dateQuestion.settings.choices[0].value, // selects the first date by default
        topic: TOPICS[this.appointmentDetails.topic],
      },
      human: {
        ...STEP_ONE.human,
        ...userDetails,
        date: dateQuestion.settings.choices[0].value,
        topic: TOPICS[this.appointmentDetails.topic]
      },
      analytics: {
        ...STEP_ONE.analytics,
        ...this.appointmentDetails,
        user_uuid: userDetails?.user_slug
      }
    };

    if (
      this.errorCode === 'UNKNOWN' ||
      this.errorCode === 'NO_TIMESLOTS'
    ) {
      const infoSection = {
        uuid: `${STEP_ONE.uuid}_info_section`,
        layout: '',
        settings: {},
        elements: [],
        info: {
          content: ERRORS[this.errorCode],
          type: 'error',
          settings: {
            align: 'top'
          }
        }
      }
      stepOne.sections.unshift(infoSection);

      // Disable questions since there are no timeslots
      const disabledSections = this.disableQuestions(stepOne.sections);

      stepOne = {
        ...stepOne,
        sections: disabledSections,
        settings: {
          ...stepOne.settings,
          disable_cta: true
        }
      }
    }

    if (!this.errorCode) {
      this.analyticsService.trackEvent('Initialise', 'AppointmentFlow', 'start-flow', stepOne.analytics);
    } else if (this.errorCode === 'NO_TIMESLOTS') {
      this.analyticsService.trackEvent('Initialise', 'AppointmentFlow', 'error-no-timeslots', stepOne.analytics);
    } else {
      this.analyticsService.trackEvent('Initialise', 'AppointmentFlow', 'error-retrieving-timeslots', stepOne.analytics, { error: timeslots.error});
    }

    return stepOne;
  }

  // Retrieves the first available timeslots from the data provided by the BE
  getAvailableTimeslots(timeslots: ITimeslots): IFilteredTimeslots[] {
    let timeslotCounter = 0;
    let result: IFilteredTimeslots[] = [];
    let currentDate: string | undefined;
    let dates = Object.keys(timeslots);
    let dateIndex = 0;

    if (dates.length === 0) {
      // use today as default to not break the page layout
      dates = [moment().format('YYYY-MM-DD')];
    };

    while (timeslotCounter < this.maxAppointmentTimeslots && dateIndex < dates.length) {
      const date = dates[dateIndex];
      const slots = timeslots[date];

      if (!currentDate || currentDate !== date) {
        result.push({ date, timeslots: [] });
        currentDate = date;
      }

      const currentGroup = result[result.length - 1];

      if (slots){
        for (const slot of slots) {
          if (timeslotCounter >= this.maxAppointmentTimeslots) break;
          currentGroup.timeslots.push(slot);
          timeslotCounter++;
        }
      }

      dateIndex++;
    }

    return result;
  }

  updateCurrentStep(payload: IFlowPayload, infoBox?: IInfoBox) {
    let updatedStep = {} as any;
    const info = {
      heading: infoBox?.infoBoxHeading || '',
      content: infoBox?.infoBoxContent || '',
      type: infoBox?.infoBoxType,
      settings: {
        align: 'top'
      }
    };

    if (this.currentStepIndex === 2) {
      const sections = this.steps[this.currentStepIndex].sections;
      sections.forEach((section: any) => {
        if (section.uuid === 'call_deflection_section') {
          section.subtext = section.subtext.replace(
            '{ partnerName }',
            this.partnerBrandService.getAttribute('name', 'partner')
          );
        }
      });
    }

    updatedStep = {
      ...this.steps[this.currentStepIndex],
      data: {
        ...this.steps[this.currentStepIndex].data,
        ...payload
      },
      human: {
        ...this.steps[this.currentStepIndex].human,
        ...payload
      },
      analytics: {
        ...this.steps[this.currentStepIndex].analytics,
      }
    };

    if (infoBox?.infoBoxContent || infoBox?.infoBoxHeading) {
      const infoSection = {
        uuid: `${this.steps[this.currentStepIndex].uuid}_info_section`,
        layout: '',
        settings: {},
        elements: [],
        info
      }
      updatedStep.sections.unshift(infoSection);
    }

    // Disable form to prevent user from booking more than the maximum allowed
    if (this.errorCode === 'MAX_TOTAL_APPOINTMENT_LIMIT') {
      const disabledSections = this.disableQuestions(updatedStep.sections);

      updatedStep = {
        ...updatedStep,
        sections: disabledSections,
        settings: {
          ...updatedStep.settings,
          disable_cta: true
        }
      }
    }

    this.steps[this.currentStepIndex] = updatedStep;
  }

  // Generates a date question based on the provided timeslots
  createDateQuestion(dates: IFilteredTimeslots[]) {
    const questionTemplate = {
      uuid: 'appointments_date_question',
      layout: 'radio_tile',
      settings: {
        choices: [],
        align: 'left'
      },
      key: 'date',
      type: 'choice',
      required: true,
    };
    const choices = [];

    for (const date of dates) {
      choices.push({
        label: moment(date.date).format('dddd'),
        value: date.date,
        text: moment(date.date).format('DD MMM')
      });
    }

    const dateQuestion = {
      ...questionTemplate,
      settings: {
        ...questionTemplate.settings,
        choices,
      },
    };

    return dateQuestion;
  }

  // Generates a timeslot question based on the provided date and timeslots.
  // Each question is: 1) A time slot with a 'show if' predicate that checks if the date is equal to the date it came from to display. 2) Has a unique key 'time_0' in order for Raptor to keep track of the questions data.
  createTimeslotQuestions(date: string, timeslots: string[]) {
    const uuid = `appointments_time_${date}`;
    const key = `${this.customTimeStringPrefix}${date}`;
    const questionTemplate = {
      uuid,
      key,
      subtext:
        'Our experts are in the Sunshine Coast, QLD. All times are AEST.',
      layout: 'dropdown',
      settings: {
        choices: []
      },
      show_if: { operands: [`<date>`, [`${date}`]], operator: 'in' },
      prompt: 'Time slot',
      type: 'choice',
      required: true,
    };

    const choices = timeslots.map((time, i: number) => {
      const formattedTime = `${time.replace('-', ' - ').toLowerCase()} AEST`;

      questionTemplate.key = `${key}-${i}`

      return {
        label: formattedTime,
        value: time
      };
    });

    const timeslotQuestion = {
      ...questionTemplate,
      settings: {
        choices
      },
    };

    return timeslotQuestion;
  }

  disableQuestions(sections: ISection[]): ISection[] {
    sections.forEach((section: ISection) => {
      if (section.elements) {
        section.elements.forEach((element: IElement) => {
          if (element.type === 'choice' || element.type === 'string') {
            element.settings.disabled = true;
          } else if (element.type === 'radio_tile') {
            element.settings.choices.forEach((choice: { disabled: boolean; }) => {
              choice.disabled = true;
            })
          }
        })
      }
    })

    return sections;
  }

  private bookAppointment(payload: IFlowPayload, resolve: (value: unknown) => void) {
    let keysToUpdate: string[] = [];
    let formattedDateTime = '';
    const bookingPayload = {
      country: COMMON_CONSTANTS.appointmentsDefaultCountry,
      state: COMMON_CONSTANTS.appointmentsDefaultState,
      partner_slug: this.partnerBrandService.getAttribute('slug', 'partner'),
      first_name: payload.first_name,
      last_name: payload.last_name,
      email: payload.email,
      phone: payload.phone,
      date: payload.date,
      // required attributes 'entity', 'entity_ref', 'topic', and 'option' is from this.appointmentsDetails
      ...this.appointmentDetails
    } as ICreateAppointmentPayload;

    // Create the formatted date for confirmation step and convert the custom time string to 'time'
    for (const key in payload) {
      if (key.includes(this.customTimeStringPrefix)) {
        keysToUpdate.push(key);
      }
    }

    if (keysToUpdate.length > 0) {
      keysToUpdate.forEach(key => {
        if (typeof payload[key] === 'string') {
          const formattedDate = moment(payload.date).format('dddd DD MMMM YYYY');
          const formattedTime = `${payload[key].replace('-', ' and ').toLowerCase()} (AEST)`;
          formattedDateTime = `${formattedDate} between ${formattedTime}`;

          bookingPayload['time'] = payload[key];
        }
      });
    }

    // Only valid entities are:
    //  'home-quote', 'home-policy', 'car-quote', 'car-policy', 'claim', 'raptor-quote', 'raptor-policy'
    //   These all need an entity_ref. Without entity_ref, passing entity is not valid
    if (!bookingPayload.entity_ref) {
      delete bookingPayload.entity;
    }

    this.appointmentService.createAppointment(bookingPayload)
      .then((response) => {
        if (response.status === 'A') {
          this.currentStepIndex++;

          this.updateCurrentStep(
            payload,
            { infoBoxHeading: `We’ll call you on ${formattedDateTime}` }
          );

          this.analyticsService.trackEvent('BookAppointment', 'AppointmentFlow', 'success', this.steps[this.currentStepIndex].analytics);

          resolve(this.steps[this.currentStepIndex]);
        } else if (response.error) {
          const { error } = response;
          let updatedStepOne =  {} as IStep;
          this.currentStepIndex = 0;
          this.errorCode = error.code ?? 'UNKNOWN';

          // get latest Timeslots for the user to retry
          this.appointmentService.getTimeslots(bookingPayload)
            .then((response) => {
              updatedStepOne = this.createStepOne(response, this.userDetails);

              this.steps[0] = updatedStepOne;

              const newPayload = Object.entries(payload).reduce<IFlowPayload>((acc, [key, value]) => {
                return key.includes('time') ? acc : { ...acc, [key]: value };
              }, {});

              this.updateCurrentStep(
                newPayload,
                { infoBoxContent: ERRORS[this.errorCode], infoBoxType: 'error' }
              );

              this.analyticsService.trackEvent('BookAppointment', 'AppointmentFlow', 'error', this.steps[this.currentStepIndex].analytics, { error: error.code } );

              resolve(this.steps[this.currentStepIndex]);
            });
        }
      });
  }
}
